import { HttpContextToken, HttpErrorResponse, HttpEventType, HttpInterceptorFn, HttpRequest } from '@angular/common/http'
import { inject, InjectionToken } from '@angular/core'
import { catchError, tap, throwError } from 'rxjs'
import { CONNECTION_PROBE, ConnectionService, ErrorCenterService } from '../services'

// Liste von Funktionen, die bestimmen, ob ein Request vom ErrorInterceptor ignoriert werden soll
// z.B. für Health-Check Requests oder andere "harmless" Requests
// Beispiel für eine Funktion, die alle Requests zu einer bestimmten URL ignoriert:
// (req: HttpRequest<unknown>) => req.url.includes('/health')
// Wenn mindestens eine Funktion true zurückgibt, wird der Request ignoriert
export const HTTP_ERROR_INTERCEPTOR_IGNORE_LIST = new InjectionToken<((req: HttpRequest<unknown>, err: HttpErrorResponse) => boolean)[]>(
  'HTTP_ERROR_INTERCEPTOR_IGNORE_LIST',
)

/**
 * Markiert eine Abfrage, die im Hintergrund laeuft und ihre Fehler selbst schluckt — etwa die stille
 * Nachlade-Leiter einer Versorgung. Sie bleibt aus der Fehlerzentrale draussen, die
 * Verbindungsueberwachung sieht sie trotzdem.
 *
 * Anlass (PROD 2026-10-07): eine Versorgung wurde geloescht, waehrend ihre Leiter noch lief; die
 * naechste Stufe holte sie 200 ms spaeter ab, bekam 404, und die Fehlerzentrale meldete einen
 * Fehler, den niemand gesehen haette. Der `catch` der Leiter kommt dafuer zu spaet — der
 * Interceptor hat den Fehler da schon eingetragen.
 */
export const STILLE_ABFRAGE = new HttpContextToken<boolean>(() => false)

/**
 * Loggt HttpFehler im ErrorCenterService und meldet jeden Ausgang an den ConnectionService.
 *
 * Der Interceptor ist der einzige Ort, der ohnehin jede Antwort sieht — deshalb haengt die
 * Verbindungsueberwachung hier und nicht an einem eigenen Health-Ping. Sie meldet bewusst auch
 * ignorierte Requests: die Ignore-Liste blendet fachliche Antworten (409) aus der Fehlerzentrale
 * aus, sagt aber nichts darueber, ob SaniUp erreichbar ist.
 */
export function errorInterceptorFn(): HttpInterceptorFn {
  return (req, next) => {
    const ignoreList = inject(HTTP_ERROR_INTERCEPTOR_IGNORE_LIST, { optional: true }) ?? []
    const errorCenterService = inject(ErrorCenterService)
    const connectionService = inject(ConnectionService)

    // Auf dem HINWEG, nicht erst im Fehlerfall: der Service merkt sich hier, welche GET-Abfrage er
    // spaeter abspielen kann, um die Rueckkehr zu belegen. Wer in einem Formular arbeitet, laedt
    // nichts mehr — es scheitert nur noch ein Speichern, und das darf er nie wiederholen.
    connectionService.noteRequest(req)

    return next(req).pipe(
      tap((event) => {
        if (event.type === HttpEventType.Response) {
          connectionService.reportSuccess()
        }
      }),
      catchError((err) => {
        connectionService.reportFailure(err)

        // Fehler in den ErrorCenterService eintragen. Der Erreichbarkeits-Versuch des
        // ConnectionService bleibt draussen: waehrend eines Ausfalls scheitert er im Takt der
        // Leiter, und das ist kein Befund, sondern das erwartete Verhalten.
        if (req.context.get(CONNECTION_PROBE) || req.context.get(STILLE_ABFRAGE) || ignoreList.some((ignoreFn) => ignoreFn(req, err))) {
          return throwError(() => err)
        }

        errorCenterService.addError(err)
        return throwError(() => err)
      }),
    )
  }
}
